import React from "react";
import { Spin, Grid } from "antd";
import { RightOutlined } from "@ant-design/icons";
import "./TopSellingMakesModelsCard.css";
import "../../../styles/dashboardCardTokens.css";
import TOP_SELLING_MOCK from "./topSellingMakesModelsMock";

const { useBreakpoint } = Grid;
const MOBILE_TOP_N = 3;

/**
 * Map API response item to table row shape.
 * New API: { rank, make_model, sold_count, median_price, median_miles, median_dom }
 * Legacy: { country, city, count, make, model, price_stats: { median }, miles_stats: { median }, dom_stats?: { median } }
 */
function mapApiToRow(item, rank) {
  if (item != null && "make_model" in item && "sold_count" in item) {
    const parts = String(item.make_model ?? "").trim().split(/\s+/);
    const make = parts[0] ?? "";
    const model = parts.slice(1).join(" ") ?? "";
    return {
      rank: item.rank ?? rank,
      make,
      model,
      sold: item.sold_count ?? 0,
      medianPrice: item.median_price ?? null,
      medianMiles: item.median_miles ?? null,
      medianDom: item.median_dom ?? null,
      tag: null,
      tagCol: null,
    };
  }
  return {
    rank,
    make: item.make ?? "",
    model: item.model ?? "",
    sold: item.count ?? 0,
    medianPrice: item.price_stats?.median ?? null,
    medianMiles: item.miles_stats?.median ?? null,
    medianDom: item.dom_stats?.median ?? null,
    tag: null,
    tagCol: null,
  };
}

/**
 * @param {object} props
 * @param {Array} [props.data]
 * @param {boolean} [props.loading]
 * @param {string} [props.city] - e.g. from user details dealership_city
 * @param {string} [props.state] - e.g. from user details dealership_state
 */
export default function TopSellingMakesModelsCard({ data = [], loading = false, city, state }) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const sourceData = Array.isArray(data) && data.length > 0 ? data : TOP_SELLING_MOCK;
  const rows = sourceData.map((item, i) => mapApiToRow(item, i + 1));
  const first = sourceData[0];
  const hasLocationInData = first && ("city" in first || "state" in first);
  const cityState =
    city != null && state != null
      ? `${city}, ${state}`
      : city != null && city !== ""
        ? city
        : hasLocationInData && rows.length > 0
          ? `${first.city ?? "Unknown"}, ${first.state ?? (first.country ?? "us").toUpperCase()}`
          : "your market";
  const subtitleLocation = state != null && state !== "" ? state : cityState;

  const displayRows = isMobile ? rows.slice(0, MOBILE_TOP_N) : rows;

  return (
    <div className="tsmm-card dashboard-card-base">
      <div className="tsmm-header dashboard-card-header">
        <div className="dashboard-card-header-left">
          <div>
            <h3 className="dashboard-card-title">What&apos;s Moving Fast In Your Market</h3>
            <p className="tsmm-subtitle">
              Best selling makes, {subtitleLocation} over past 90 days
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="tsmm-loading">
          <Spin />
        </div>
      ) : isMobile ? (
        <div className="tsmm-mobile-list">
          {displayRows.length === 0 ? (
            <div className="tsmm-empty">No top selling data yet.</div>
          ) : (
            displayRows.map((row) => (
                <div key={row.rank} className="tsmm-mobile-item">
                  <div className="tsmm-mobile-item-main">
                    <span className="tsmm-mobile-rank-badge">#{row.rank}</span>
                    <div className="tsmm-mobile-item-body">
                      <div className="tsmm-mobile-item-name">{row.make} {row.model}</div>
                      <div className="tsmm-mobile-item-stats">
                        <span className="tsmm-mobile-sold">{row.sold} sold</span>
                        <span className="tsmm-mobile-dom">{row.medianDom != null ? `${row.medianDom} Avg DOM` : "— Avg DOM"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
      ) : (
      <div className="tsmm-table-wrap">
        <table className="tsmm-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Make & Model</th>
              <th>Sold</th>
              <th>Median Price</th>
              <th>Median Miles</th>
              <th>Median DOM</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="tsmm-empty">
                  No top selling data yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
              <tr key={row.rank}>
                <td>
                  <span className="tsmm-rank-badge">#{row.rank}</span>
                </td>
                <td>
                  <div className="tsmm-make-model">
                    <span className="tsmm-make-logo">{row.make.charAt(0)}</span>
                    <span>{row.make} {row.model}</span>
                  </div>
                </td>
                <td>{row.sold}</td>
                <td>{row.medianPrice != null ? `$${Number(row.medianPrice).toLocaleString()}` : "—"}</td>
                <td>
                  <span>{row.medianMiles != null ? `${Number(row.medianMiles).toLocaleString()} mi` : "—"}</span>
                  {row.tag && row.tagCol === "miles" && (
                    <span className={`tsmm-tag tsmm-tag--${row.tag === "LOW CPO SAMPLE" ? "grey" : "yellow"}`}>
                      {row.tag}
                    </span>
                  )}
                </td>
                <td>
                  {row.medianDom != null && (
                    <>
                      <span>{row.medianDom} DOM</span>
                      <RightOutlined className="tsmm-arrow" />
                    </>
                  )}
                  {row.tag && row.tagCol === "dom" && (
                    <>
                      <span className="tsmm-tag tsmm-tag--purple">{row.tag}</span>
                      <RightOutlined className="tsmm-arrow" />
                    </>
                  )}
                  {row.medianDom == null && !(row.tag && row.tagCol === "dom") && (
                    <>
                      <span>—</span>
                      <RightOutlined className="tsmm-arrow" />
                    </>
                  )}
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
